import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Cal, { getCalApi } from "@calcom/embed-react";
import { getBookingContext } from "@/lib/schedulingApi";
import { Loader2, AlertCircle, CheckCircle, Calendar, Video, ExternalLink } from "lucide-react";

export default function SchedulePage() {
  const { token } = useParams();
  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await getBookingContext(token);
        setCtx(data);
      } catch (e) {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  // Initialize Cal embed UI theme
  useEffect(() => {
    (async () => {
      try {
        const cal = await getCalApi();
        cal("ui", {
          theme: "light",
          styles: { branding: { brandColor: "#C2410C" } },
          hideEventTypeDetails: false,
          layout: "month_view",
        });
      } catch (e) {}
    })();
  }, [ctx]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#C2410C]" size={32} />
      </div>
    );
  }

  if (notFound || !ctx) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex flex-col items-center justify-center gap-4 px-4">
        <AlertCircle size={40} className="text-stone-400" />
        <h1 className="font-display text-3xl text-stone-800">Booking link not found</h1>
        <p className="text-stone-500 text-center max-w-md">
          This interview booking link is invalid or has expired. Please check with the recruiter.
        </p>
      </div>
    );
  }

  const alreadyBooked = ctx.status === "booked" || ctx.status === "rescheduled";

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Navbar */}
      <nav className="border-b border-stone-200 bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <span className="font-display text-xl tracking-tight text-stone-900">Wishire</span>
          <span className="text-xs text-stone-400 font-mono uppercase tracking-widest">Interview scheduling</span>
        </div>
      </nav>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10 md:py-14">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex p-3 rounded-2xl bg-amber-100 text-[#C2410C] mb-4">
            <Calendar size={22} />
          </div>
          <h1 className="font-display text-3xl md:text-4xl text-stone-900 leading-tight">
            {alreadyBooked ? "Your interview is booked" : "Pick a time for your interview"}
          </h1>
          <p className="mt-3 text-stone-600">
            {ctx.job_title ? <><strong>{ctx.job_title}</strong> at </> : null}
            <strong>{ctx.company_name}</strong> · {ctx.duration_min} min {ctx.mode} interview
          </p>
        </div>

        {alreadyBooked ? (
          <div className="rounded-3xl bg-white border border-emerald-200 p-8 text-center">
            <CheckCircle size={36} className="text-emerald-500 mx-auto mb-4" />
            <p className="font-display text-2xl text-stone-900">You're all set!</p>
            {ctx.scheduled_at && (
              <p className="text-stone-600 mt-2">
                {new Date(ctx.scheduled_at).toLocaleString("en-IN", { dateStyle: "full", timeStyle: "short" })}
              </p>
            )}
            {ctx.meeting_url && (
              <a
                href={ctx.meeting_url}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#C2410C] text-white hover:bg-orange-700 transition-colors font-medium"
              >
                <Video size={16} /> Join meeting <ExternalLink size={14} />
              </a>
            )}
            <p className="text-stone-400 text-sm mt-4">
              A calendar invite has been sent to {ctx.candidate_email}. Need to reschedule? Use the link in your calendar invite.
            </p>
          </div>
        ) : (
          <div className="rounded-3xl bg-white border border-stone-200 overflow-hidden shadow-sm">
            <Cal
              calLink={ctx.cal_link}
              style={{ width: "100%", height: "100%", minHeight: "640px", overflow: "scroll" }}
              config={{
                name: ctx.candidate_name || "",
                email: ctx.candidate_email || "",
                theme: "light",
                "metadata[wishire_token]": token,
              }}
            />
          </div>
        )}

        <p className="text-center text-stone-400 text-sm mt-8">
          Powered by Wishire · scheduling via Cal.com
        </p>
      </main>
    </div>
  );
}
